import { useEffect, useMemo, useState } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import type { TouchpointSourceType } from '@trello/ad-sequencer';
import { isDesktop } from '@trello/browser';
import type { ExperimentVariations } from '@trello/feature-gate-client';
import { useHasValidAaSession } from '@trello/heartbeat/session';

import {
  type TrelloCrossFlowExperiment,
  trelloCrossFlowOpen,
} from '../TrelloCrossFlow';
import { useHasEligibleWorkspaces } from './runtimeEligibilityChecks/useHasEligibleWorkspaces';
import { useHasNoAvailableSites } from './runtimeEligibilityChecks/useHasNoAvailableSites';
import { useIsCrossFlowMemberConfirmed } from './runtimeEligibilityChecks/useIsCrossFlowMemberConfirmed';
import { getSporkCffeExperimentalOption } from './getSporkCffeExperimentalOption';
import { useTrelloCrossFlowExperiment } from './useTrelloCrossFlowExperiment';

const experimentFeatureKey =
  'trello_xf_spork_branding_positioning_single_player';

export type CrossFlowForSporkSinglePlayerExperimentVariations =
  ExperimentVariations<typeof experimentFeatureKey, 'cohort'>;

export interface CrossFlowForSinglePlayerSporkParams {
  source: TouchpointSourceType;
  workspaceId?: string;
  boardId?: string;
}

export const useCrossFlowForSinglePlayerSpork = ({
  source,
  workspaceId,
  boardId,
}: CrossFlowForSinglePlayerSporkParams): TrelloCrossFlowExperiment<CrossFlowForSporkSinglePlayerExperimentVariations> => {
  const crossFlow = useCrossFlow();

  // 1. Check that the member is confirmed
  const { isEligible: isMemberConfirmed } = useIsCrossFlowMemberConfirmed();

  // 2. Check that the member has eligible workspaces
  const { isEligible: hasEligibleWorkspaces, eligibleWorkspaceOptions } =
    useHasEligibleWorkspaces({
      workspaceId,
      entitlementRequired: 'free',
    });

  // 3. Check that the user is not on desktop
  const isWeb = !isDesktop();

  const [
    isImmediateRuntimeEligibilityChecksPassed,
    setImmediateRuntimeEligibilityChecksPassed,
  ] = useState(false);
  // 4. Check that the member has no available sites
  const { isEligible: hasNoAvailableSites } = useHasNoAvailableSites({
    doLoadAvailableSites: isImmediateRuntimeEligibilityChecksPassed,
    source,
  });

  const experiment = useTrelloCrossFlowExperiment({
    experimentFeatureKey,
    source,
    eligibilityCheckResults: [
      isMemberConfirmed,
      hasEligibleWorkspaces,
      crossFlow.isEnabled,
      isWeb,
    ],
    deferredEligibilityCheckResults: [hasNoAvailableSites],
  });

  useEffect(() => {
    setImmediateRuntimeEligibilityChecksPassed(
      experiment.isImmediateRuntimeEligibilityChecksPassed,
    );
  }, [experiment.isImmediateRuntimeEligibilityChecksPassed]);

  const [hasValidAaSession] = useHasValidAaSession();

  return useMemo(
    () => ({
      ...experiment,
      open: trelloCrossFlowOpen(
        crossFlow,
        source,
        eligibleWorkspaceOptions,
        hasValidAaSession,
        {
          workspaceId,
          boardId,
          ...getSporkCffeExperimentalOption(experiment.cohort),
        },
      ),
    }),
    [
      boardId,
      hasValidAaSession,
      crossFlow,
      eligibleWorkspaceOptions,
      experiment,
      source,
      workspaceId,
    ],
  );
};
